import React, { useState, useEffect } from 'react';
import { X, User, Plus, Check, Search, Mail, Phone, MapPin, Upload, AlertCircle } from 'lucide-react';
import { eventParticipantsApi, contactApi } from '../lib/api';
import type { Tables } from '../lib/types';
import NewClientModal from './NewClientModal';
import ImportContactsModal from './ImportContactsModal';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface ViewClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Tables<'events'> | null;
  onRemoveParticipant: (eventId: string, participantId: string) => void;
  onAddParticipant: (eventId: string, participantCount: number) => void;
}

const ViewClientsModal: React.FC<ViewClientsModalProps> = ({
  isOpen,
  onClose,
  event,
  onRemoveParticipant,
  onAddParticipant
}) => {
  const { user } = useAuth();
  const [clients, setClients] = useState<any[]>([]);
  const [availableContacts, setAvailableContacts] = useState<Tables<'contacts'>[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [showSelection, setShowSelection] = useState(false);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState<any>(null);
  const [loadingCreator, setLoadingCreator] = useState(false);

  useEffect(() => {
    if (isOpen && event?.id) {
      loadData();
    }
  }, [isOpen, event?.id]);

  useEffect(() => {
    if (selectedClient?.created_by) {
      loadCreatorInfo(selectedClient.created_by);
    } else {
      setCreatorInfo(null);
    }
  }, [selectedClient]);

  const loadData = async () => {
    if (!event?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      const [clientsData, contactsData] = await Promise.all([
        eventParticipantsApi.getByEventId(event.id),
        contactApi.getAll()
      ]);

      const clientsWithTempNumbers = clientsData.map((p, index) => ({
        ...p,
        temp_number: index + 1
      }));

      setClients(clientsWithTempNumbers);

      const clientIds = new Set(clientsData.map(p => p.participant_id));
      const availableContacts = contactsData
        .filter(contact => !clientIds.has(contact.id))
        .filter(contact => {
          if (user?.role === 'admin') return true;
          return contact.city === event.city;
        });

      setAvailableContacts(availableContacts);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const loadCreatorInfo = async (creatorId: string) => {
    try {
      setLoadingCreator(true);
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('name, role')
        .eq('id', creatorId)
        .single();

      if (error) throw error;
      setCreatorInfo(profile);
    } catch (error) {
      console.error('Error loading creator info:', error);
      setCreatorInfo(null);
    } finally {
      setLoadingCreator(false);
    }
  };

  const calculateAge = (birthdate: string) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const isInAgeRange = (birthdate: string, ageRange: string) => {
    const age = calculateAge(birthdate);

    if (!ageRange) return true;

    if (ageRange.startsWith('over-')) {
      const minAge = parseInt(ageRange.replace('over-', ''), 10);
      return age > minAge;
    }

    const parts = ageRange.split('-');
    if (parts.length === 2) {
      const min = parseInt(parts[0], 10);
      const max = parseInt(parts[1], 10);
      if (!isNaN(min) && !isNaN(max)) {
        return age >= min && age <= max;
      }
    }

    return true;
  };

  const handleToggleContact = (contactId: string) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        if (!event) return prev;
        const currentClients = clients.length;
        const selectedCount = prev.length;
        if (currentClients + selectedCount + 1 <= event.max_participants) {
          return [...prev, contactId];
        }
        alert('Hai raggiunto il numero massimo di partecipanti per questo evento');
        return prev;
      }
    });
  };

  const handleAddSelectedContacts = async () => {
    if (!event?.id || selectedContacts.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const currentCount = clients.length;

      for (let i = 0; i < selectedContacts.length; i++) {
        const contactId = selectedContacts[i];
        const participantNumber = currentCount + i + 1;

        await supabase
          .from('event_participants')
          .insert({
            event_id: event.id,
            participant_id: contactId,
            participant_number: participantNumber
          });
      }

      // Update local state first
      const newClients = await eventParticipantsApi.getByEventId(event.id);
      const clientsWithTempNumbers = newClients.map((p, index) => ({
        ...p,
        temp_number: index + 1
      }));
      setClients(clientsWithTempNumbers);

      // Notify parent component of the new count
      onAddParticipant(event.id, newClients.length);

      setSelectedContacts([]);
      setShowSelection(false);
      setSearchQuery('');

      // Reload available contacts
      const contactsData = await contactApi.getAll();
      const clientIds = new Set(newClients.map(p => p.participant_id));
      const availableContacts = contactsData
        .filter(contact => !clientIds.has(contact.id))
        .filter(contact => {
          if (user?.role === 'admin') return true;
          return contact.city === event.city;
        });
      setAvailableContacts(availableContacts);
    } catch (error: any) {
      console.error('Error adding clients:', error);
      setError(error.message || 'Errore nell\'aggiunta dei partecipanti');
    } finally {
      setLoading(false);
    }
  };

  const handleNewClient = async (clientData: any) => {
    if (!event?.id) return;

    try {
      setLoading(true);
      setError(null);

      const contact = await contactApi.create({
        name: clientData.name.toUpperCase(),
        email: clientData.email,
        phone: clientData.phone,
        gender: clientData.gender,
        birthdate: clientData.birthdate,
        city: event.city
      });

      const participantNumber = clients.length + 1;

      await supabase
        .from('event_participants')
        .insert({
          event_id: event.id,
          participant_id: contact.id,
          participant_number: participantNumber
        });

      // Update local state first
      const newClients = await eventParticipantsApi.getByEventId(event.id);
      const clientsWithTempNumbers = newClients.map((p, index) => ({
        ...p,
        temp_number: index + 1
      }));
      setClients(clientsWithTempNumbers);

      // Notify parent component of the new count
      onAddParticipant(event.id, newClients.length);

      setShowNewClientForm(false);
    } catch (error: any) {
      console.error('Error creating new client:', error);
      setError(error.message || 'Errore nella creazione del nuovo partecipante');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClient = async (clientId: string) => {
    if (!event?.id) return;

    if (window.confirm('Sei sicuro di voler rimuovere questo partecipante dall\'evento?')) {
      try {
        setLoading(true);
        setError(null);

        await eventParticipantsApi.remove(event.id, clientId);

        // Update local state first
        const updatedClients = clients.filter(p => p.participant_id !== clientId);
        const clientsWithTempNumbers = updatedClients.map((p, index) => ({
          ...p,
          temp_number: index + 1
        }));
        setClients(clientsWithTempNumbers);

        // Notify parent component of the new count
        onRemoveParticipant(event.id, clientId);
        onAddParticipant(event.id, updatedClients.length);

        // Update available contacts
        const contactsData = await contactApi.getAll();
        const clientIds = new Set(updatedClients.map(p => p.participant_id));
        const availableContacts = contactsData
          .filter(contact => !clientIds.has(contact.id))
          .filter(contact => {
            if (user?.role === 'admin') return true;
            return contact.city === event.city;
          });
        setAvailableContacts(availableContacts);
      } catch (error: any) {
        console.error('Error removing client:', error);
        setError(error.message || 'Errore nella rimozione del partecipante');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewDetails = (client: any) => {
    setSelectedClient(client.participant);
    setShowDetailsModal(true);
  };

  const filteredContacts = availableContacts.filter(contact => {
    const q = searchQuery.toLowerCase();
    return (
      contact.name.toLowerCase().includes(q) ||
      (contact.email && contact.email.toLowerCase().includes(q)) ||
      (contact.phone && contact.phone.toLowerCase().includes(q))
    );
  });

  if (!isOpen || !event) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">Partecipanti all'evento</h2>
            <p className="text-gray-600">{event.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-gray-600">
            {clients.length} / {event.max_participants} partecipanti
            {event.max_participants - clients.length > 0 &&
              ` (${event.max_participants - clients.length} posti disponibili)`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowSelection(true);
                setShowNewClientForm(false);
              }}
              className="bg-rose-600 text-white px-3 py-1.5 rounded-lg hover:bg-rose-700 transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={event.max_participants <= clients.length || loading}
            >
              <Plus className="h-4 w-4" />
              Seleziona Esistenti
            </button>
            <button
              onClick={() => {
                setShowNewClientForm(true);
                setShowSelection(false);
              }}
              className="bg-rose-600 text-white px-3 py-1.5 rounded-lg hover:bg-rose-700 transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={event.max_participants <= clients.length || loading}
            >
              <Plus className="h-4 w-4" />
              Nuovo Partecipante
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Caricamento...</p>
          </div>
        ) : showNewClientForm ? (
          <NewClientModal
            isOpen={true}
            onClose={() => setShowNewClientForm(false)}
            onSubmit={handleNewClient}
          />
        ) : showSelection ? (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">Seleziona Partecipanti</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowSelection(false);
                    setSelectedContacts([]);
                    setSearchQuery('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annulla
                </button>
                <button
                  onClick={handleAddSelectedContacts}
                  disabled={selectedContacts.length === 0 || loading}
                  className="bg-rose-600 text-white px-3 py-1.5 rounded-lg hover:bg-rose-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Aggiungi Selezionati ({selectedContacts.length})
                </button>
              </div>
            </div>

            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Cerca partecipanti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-rose-500 focus:border-rose-500"
              />
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
              {filteredContacts.length > 0 ? (
                filteredContacts.map((contact) => {
                  const isMan = contact.gender.toLowerCase().includes('uomo');
                  const price = isMan ? event.men_price : event.women_price;

                  return (
                    <div
                      key={contact.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                      onClick={() => handleToggleContact(contact.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          selectedContacts.includes(contact.id)
                            ? 'bg-rose-100 text-rose-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {selectedContacts.includes(contact.id) ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <User className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{contact.name}</p>
                          <p className="text-sm text-gray-500">
                            {({'uomo_etero': 'Uomo Etero', 'donna_etero': 'Donna Etero', 'uomo_gay': 'Uomo Gay', 'donna_gay': 'Donna Gay', 'non_dichiarato': 'Non dichiarato'} as Record<string, string>)[contact.gender] || contact.gender} -
                            {calculateAge(contact.birthdate)} anni
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-rose-600">€{Number(price).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center py-4 text-gray-500">
                  {searchQuery ? 'Nessun risultato trovato' : 'Nessun partecipante disponibile'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.length > 0 ? (
              clients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-rose-50 rounded-full">
                        <User className="h-5 w-5 text-rose-500" />
                      </div>
                      <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full">
                        <span className="font-semibold text-gray-700">#{client.temp_number}</span>
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => handleViewDetails(client)}
                        className="font-medium hover:text-rose-600 transition-colors"
                      >
                        {client.participant.name}
                      </button>
                      <p className="text-sm text-gray-500">
                        {({'uomo_etero': 'Uomo Etero', 'donna_etero': 'Donna Etero', 'uomo_gay': 'Uomo Gay', 'donna_gay': 'Donna Gay', 'non_dichiarato': 'Non dichiarato'} as Record<string, string>)[client.participant.gender] || client.participant.gender} -
                        {calculateAge(client.participant.birthdate)} anni
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveClient(client.participant_id)}
                    className="text-rose-600 hover:text-rose-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    Rimuovi
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Nessun partecipante registrato</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showDetailsModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Dettagli Partecipante</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedClient(null);
                  setCreatorInfo(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-50 rounded-full">
                  <User className="h-5 w-5 text-rose-500" />
                </div>
                <div>
                  <p className="font-medium">{selectedClient.name}</p>
                  <p className="text-sm text-gray-500 capitalize">
                    {({'uomo_etero': 'Uomo Etero', 'donna_etero': 'Donna Etero', 'uomo_gay': 'Uomo Gay', 'donna_gay': 'Donna Gay', 'non_dichiarato': 'Non dichiarato'} as Record<string, string>)[selectedClient.gender] || selectedClient.gender}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{selectedClient.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span>{selectedClient.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{selectedClient.city}</span>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Data di Nascita</p>
                    <p className="font-medium">{new Date(selectedClient.birthdate).toLocaleDateString('it-IT')}</p>
                    <p className="text-sm text-gray-500">({calculateAge(selectedClient.birthdate)} anni)</p>
                  </div>
                </div>
              </div>

              {selectedClient.created_by && creatorInfo && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-500">Scheda creata da</p>
                  <div className="flex items-center gap-2 mt-1">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{creatorInfo.name}</span>
                    <span className="text-sm text-gray-500 capitalize">({creatorInfo.role})</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <NewClientModal
        isOpen={showNewClientForm}
        onClose={() => setShowNewClientForm(false)}
        onSubmit={handleNewClient}
      />

      <ImportContactsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={loadData}
        city={event.city}
      />
    </div>
  );
};

export default ViewClientsModal;